import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Coins, X, Search, Check } from "lucide-react";
import { currencies, findCurrency } from "./venue-form/currency-data";
import { useViewerCurrencyStore } from "@/store/viewerCurrencyStore";

interface CurrencyPickerProps {
  triggerClassName?: string;
  isLight?: boolean;
}

/** Footer control letting a viewer override the auto-detected display currency (mirrors Airbnb's picker). */
export function CurrencyPicker({ triggerClassName, isLight }: CurrencyPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const currencyCode = useViewerCurrencyStore((s) => s.currencyCode);
  const setCurrency = useViewerCurrencyStore((s) => s.setCurrency);

  const current = currencyCode ? findCurrency(currencyCode) : null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return currencies;
    return currencies.filter(
      (c) => c.name.toLowerCase().includes(q) || c.value.toLowerCase().includes(q)
    );
  }, [query]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          triggerClassName ??
          `flex items-center gap-1.5 transition-colors px-3 py-1 ${isLight ? "text-gray-400 hover:text-gray-900" : "text-gray-500 hover:text-white"}`
        }
      >
        <Coins size={13} />
        {current ? current.value : "Currency"}
      </button>

      {open &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setOpen(false)}>
            <div
              className={`w-full max-w-2xl max-h-[80vh] rounded-2xl overflow-hidden flex flex-col ${isLight ? "bg-white" : "bg-[#0f0f0f] border border-white/10"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={`flex items-center justify-between px-6 py-5 border-b ${isLight ? "border-gray-200" : "border-white/10"}`}>
                <h2 className={`text-lg font-bold ${isLight ? "text-gray-900" : "text-white"}`}>Choose a currency</h2>
                <button onClick={() => setOpen(false)} className={isLight ? "text-gray-400 hover:text-gray-900" : "text-gray-500 hover:text-white"}>
                  <X size={20} />
                </button>
              </div>

              <div className={`px-6 py-4 border-b ${isLight ? "border-gray-200" : "border-white/10"}`}>
                <div className="relative">
                  <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isLight ? "text-gray-400" : "text-gray-500"}`} />
                  <input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search currencies..."
                    className={`w-full pl-9 pr-3 py-2.5 rounded-lg text-sm focus:outline-none ${
                      isLight ? "bg-gray-100 text-gray-900 placeholder-gray-400" : "bg-white/5 text-white placeholder-gray-600 border border-white/10"
                    }`}
                  />
                </div>
              </div>

              <div className="overflow-y-auto p-4 grid grid-cols-1 sm:grid-cols-2 gap-1">
                {filtered.map((c) => {
                  const isSelected = c.value === currencyCode;
                  return (
                    <button
                      key={c.value}
                      onClick={() => {
                        setCurrency(c.value);
                        setOpen(false);
                      }}
                      className={`flex items-center justify-between gap-2 px-4 py-3 rounded-lg text-left transition-colors ${
                        isSelected
                          ? "bg-[#ff3366]/10 border border-[#ff3366]/40"
                          : isLight
                            ? "hover:bg-gray-100 border border-transparent"
                            : "hover:bg-white/5 border border-transparent"
                      }`}
                    >
                      <div>
                        <div className={`text-sm font-medium ${isLight ? "text-gray-900" : "text-white"}`}>{c.name}</div>
                        <div className={`text-xs ${isLight ? "text-gray-500" : "text-gray-500"}`}>
                          {c.value} — {c.symbol}
                        </div>
                      </div>
                      {isSelected && <Check size={16} className="text-[#ff3366] shrink-0" />}
                    </button>
                  );
                })}
                {filtered.length === 0 && (
                  <p className={`col-span-2 text-center py-8 text-sm ${isLight ? "text-gray-400" : "text-gray-500"}`}>No currencies match "{query}"</p>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

export default CurrencyPicker;
